import { useTranslation } from "react-i18next";
import {
  Download,
  FileText,
  Film,
  Music,
  Paperclip,
  X,
  type LucideIcon,
} from "lucide-react";
import type { FeedbackAttachment } from "@stockline/shared/types/core/feedback";
import { formatBytes } from "../lib/file-attach";

function isImage(att: FeedbackAttachment): boolean {
  return att.mime.startsWith("image/");
}

function iconFor(mime: string): LucideIcon {
  if (mime.startsWith("video/")) return Film;
  if (mime.startsWith("audio/")) return Music;
  if (mime === "application/pdf" || mime.startsWith("text/")) return FileText;
  return Paperclip;
}

export interface AttachmentListProps {
  attachments: FeedbackAttachment[];
  /** When set, renders a remove-X over each item that calls this on click. */
  onRemove?: (index: number) => void;
  /** Compact reply-row variant — single-row scroll, smaller thumbnails. */
  inline?: boolean;
}

export function AttachmentList({
  attachments,
  onRemove,
  inline = false,
}: AttachmentListProps) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;

  const images = attachments
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => isImage(a));
  const files = attachments
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => !isImage(a));

  return (
    <div className={inline ? "space-y-1.5" : "space-y-2"}>
      {images.length > 0 && (
        <div
          className={
            inline ? "flex gap-1.5 overflow-x-auto" : "grid grid-cols-3 gap-2"
          }
        >
          {images.map(({ a, i }) => (
            <a
              key={i}
              href={a.dataUrl}
              target="_blank"
              rel="noreferrer"
              className={`group relative overflow-hidden rounded-md border bg-muted ${
                inline ? "h-14 w-20 shrink-0" : "block"
              }`}
            >
              <img
                src={a.dataUrl}
                alt={a.name}
                className={
                  inline
                    ? "h-full w-full object-cover"
                    : "block aspect-video w-full object-cover"
                }
              />
              {onRemove && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onRemove(i);
                  }}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100"
                  aria-label={t("feedback.remove_attachment", "Remove")}
                >
                  <X size={11} />
                </button>
              )}
            </a>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div
          className={inline ? "flex flex-wrap gap-1.5" : "flex flex-wrap gap-2"}
        >
          {files.map(({ a, i }) => {
            const Icon = iconFor(a.mime);
            return (
              <a
                key={i}
                href={a.dataUrl}
                download={a.name}
                className="group inline-flex items-center gap-2 rounded-md border bg-background px-2 py-1 text-xs transition hover:border-emerald-300 hover:bg-emerald-50"
                title={`${a.name} · ${formatBytes(a.size)}`}
              >
                <Icon size={13} className="shrink-0 text-slate-500" />
                <span className="max-w-[160px] truncate font-medium text-slate-700">
                  {a.name}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {formatBytes(a.size)}
                </span>
                {onRemove ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onRemove(i);
                    }}
                    className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={t("feedback.remove_attachment", "Remove")}
                  >
                    <X size={11} />
                  </button>
                ) : (
                  <Download
                    size={11}
                    className="text-muted-foreground transition group-hover:text-emerald-700"
                  />
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
